import dayjs, { Dayjs } from 'dayjs';
import { ParticipantsAndRatesTable, ResourceAllocationPlanTable } from '../interfaces/interfaces';
import { addOrSubtractDays, addOrSubtractDaysForEndDate, calculateStartDate, calculateEndDate } from './services';
import { normalizeResourcePlan } from './resourcePlan';

export interface CopyResource {
    name: string; resource_type: string; region: string; service_line: string;
    function: string; career_level: string; start_date: string; end_date: string;
    allocation: number; comment: string;
    proposed_price_per_hour?: number | null; monthly_wise_allocation?: Record<string, number>;
    booking_types?: string[];
    booking_ids?: number[];
    issues?: string[];
    source_details?: Record<string, string | null>;
}
export interface CopySource {
    project_start_date: string | null; version_id: number | null;
    duration_in_months: number | null;
    resources: CopyResource[];
    booking_count?: number;
    skipped_booking_count?: number;
}
export interface CopyRate {
    resource_type: string; region: string; service_line: string; function: string; career_level: string;
    cost_per_hour: number; target_client_margin: number; min_fte_allocation: number;
}
export const matchingRate = (r: CopyResource, rates: CopyRate[]) => rates.findIndex(p =>
    p.resource_type === r.resource_type && p.region === r.region && p.service_line === r.service_line &&
    p.function === r.function && p.career_level === r.career_level);

export function copyResourceRows(rows: CopyResource[], rates: CopyRate[], sourceStart: string | null,
    start: Dayjs, end: Dayjs, duration: number, firstKey = 1) {
    const anchor = dayjs(sourceStart?.slice(0, 10));
    const shift = sourceStart && anchor.isValid() ? start.diff(anchor, 'day') : 0;
    const participants: ParticipantsAndRatesTable[] = [];
    const allocations: ResourceAllocationPlanTable[] = [];
    rows.forEach((row, index) => {
        const rate = rates[matchingRate(row, rates)] ?? { ...row,
            resource_type: row.resource_type || '', region: row.region || '', service_line: row.service_line || '',
            function: row.function || '', career_level: row.career_level || '',
            cost_per_hour: 0, target_client_margin: 0, min_fte_allocation: 0 };
        const allocation = Number.isFinite(row.allocation) ? Math.max(0, Math.min(100, row.allocation)) : 100;
        const oldStartDate = dayjs(row.start_date?.slice(0, 10));
        const oldEndDate = dayjs(row.end_date?.slice(0, 10));
        let from = start, to = end;
        if (sourceStart && anchor.isValid() && row.start_date && row.end_date && oldStartDate.isValid() && oldEndDate.isValid()) {
            const projectStart = start.format('YYYY-MM-DD');
            from = dayjs(calculateStartDate(projectStart, duration, addOrSubtractDays(oldStartDate.format('YYYY-MM-DD'), shift)));
            to = dayjs(calculateEndDate(projectStart, duration, addOrSubtractDaysForEndDate(
                oldEndDate.format('YYYY-MM-DD'), shift, projectStart, anchor.format('YYYY-MM-DD'), oldStartDate.format('YYYY-MM-DD'))));
        }
        const key = firstKey + index;
        const margin = Number((rate.target_client_margin * 100).toFixed(2));
        const price = margin < 100 ? Number((rate.cost_per_hour / (1 - margin / 100)).toFixed(2)) : 0;
        const proposedPrice = row.proposed_price_per_hour ?? price;
        const proposedMargin = proposedPrice > 0 ? Number((100 - rate.cost_per_hour / proposedPrice * 100).toFixed(2)) : 0;
        participants.push({ key, oldKey: key, resourceType: rate.resource_type, selectRegion: rate.region,
            serviceLineCapability: rate.service_line, function: rate.function, selectLevel: rate.career_level,
            loadedLaborCostHr: rate.cost_per_hour, targetClientMargin: margin, proposedPriceHr: proposedPrice,
            targetPriceHr: price, clientMarginProposed: proposedMargin, varianceTargetMargin: proposedMargin - margin,
            minFTE: rate.min_fte_allocation ?? 0 });
        const plan = normalizeResourcePlan({ key, oldKey: key, role: `${rate.resource_type} - ${rate.region} - ${rate.service_line} - ${rate.career_level}`,
            allocation, startDate: from, endDate: to, duration: to.add(1, 'day').diff(from, 'month', true),
            oldStartDate, oldEndDate, months: row.monthly_wise_allocation ?? {},
            name: row.name, comment: row.comment, startDateWarning: false, endDateWarning: false,
            minFTE: rate.min_fte_allocation ?? 0 }, start, end.startOf('month').diff(start.startOf('month'), 'month') + 1);
        plan.duration = plan.endDate.add(1, 'day').diff(plan.startDate.startOf('day'), 'month', true);
        allocations.push(plan);
    });
    return { participants, allocations };
}
